import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";
import { REPORT_GATE } from "../screens/Report";

/* ──────────────────────────────────────────────────────────────────
 * ReportCard — Home's calm entry point to the Signal Report.
 *
 * The June cohort found the share flow at near-zero rates because the
 * report simply wasn't reachable from Home. This card fixes discovery
 * without cluttering the launchpad: one quiet surface-toned card, and
 * it renders NOTHING for a brand-new user (0 check-ins) — their Home
 * is unchanged and already pushes the first check-in.
 *
 * States (driven by totalSnapshots, same ≥3 threshold as the backend
 * summary gate):
 *   0            → null. Home byte-identical to the pre-report world.
 *   1..GATE-1    → locked line: "unlocks after N more check-ins".
 *                  Deliberately NOT a share CTA — a day-1 user
 *                  shouldn't hand anyone a near-empty report.
 *   ≥ GATE       → entry card → /report (preview-first; no link is
 *                  minted from Home).
 *
 * Pre-session variant: when Profile's care-team setting says the user
 * sees a provider AND their session day is today or tomorrow, the
 * unlocked card swaps to "Session coming up" copy. Same single card —
 * Home's footprint is identical, only the words lean in. Deterministic
 * local computation; no new surface.
 * ────────────────────────────────────────────────────────────────── */

// profile_json.session_day uses JS Date#getDay() numbering (0=Sunday).
function sessionSoon(profile, now = new Date()) {
  if (!profile?.sees_provider) return false;
  const day = profile.session_day;
  if (typeof day !== "number" || day < 0 || day > 6) return false;
  const today = now.getDay();
  const tomorrow = (today + 1) % 7;
  return day === today || day === tomorrow;
}

export default function ReportCard({ totalSnapshots, user }) {
  const navigate = useNavigate();
  const count = totalSnapshots ?? 0;

  if (count <= 0) return null;

  // Locked — quiet unlock line, no share affordance.
  if (count < REPORT_GATE) {
    const remaining = REPORT_GATE - count;
    return (
      <div style={{
        background: PALETTE.surface, border: `1px solid ${PALETTE.border}`,
        borderRadius: 16, padding: "15px 18px",
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <LockIcon />
        <p style={{ fontSize: 12.5, color: PALETTE.textMid, fontWeight: 300, lineHeight: 1.55 }}>
          Your report unlocks after {remaining} more {remaining === 1 ? "check-in" : "check-ins"}.
        </p>
      </div>
    );
  }

  const preSession = sessionSoon(user?.profile_json);

  return (
    <button
      onClick={() => navigate("/report")}
      aria-label="View your signal report"
      style={{
        width: "100%", textAlign: "left",
        background: PALETTE.surface, border: `1px solid ${PALETTE.border}`,
        borderRadius: 16, padding: "17px 18px",
        cursor: "pointer", fontFamily: "inherit",
        display: "flex", alignItems: "center", gap: 14,
        boxShadow: "0 1px 6px rgba(28,24,20,0.04)",
        transition: "box-shadow 0.15s ease",
      }}
    >
      <div style={{
        width: 38, height: 38, borderRadius: 12, flexShrink: 0,
        background: PALETTE.primarySoft,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <ReportIcon />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.primary, fontWeight: 600, marginBottom: 4 }}>
          {preSession ? "Reminder" : "Your Signal Report"}
        </div>
        <p style={{ fontSize: 12.5, color: PALETTE.textMid, fontWeight: 300, lineHeight: 1.5 }}>
          {preSession
            ? "Want to share your latest summary today?"
            : "A 30-day summary you can review and share with anyone you choose."}
        </p>
      </div>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={PALETTE.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

function ReportIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke={PALETTE.primary} strokeWidth="1.5" aria-hidden="true">
      <path d="M5 3h7l3 3v11a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z" strokeLinejoin="round" />
      <path d="M7 13l2-2.5 1.5 1.5L13 9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke={PALETTE.textMute} strokeWidth="1.5" style={{ flexShrink: 0 }} aria-hidden="true">
      <rect x="4.5" y="9" width="11" height="8" rx="2" strokeLinejoin="round" />
      <path d="M7 9V6.5a3 3 0 016 0V9" strokeLinecap="round" />
    </svg>
  );
}
